/**
 * Shared journey endpoint used by visit choices and the final assignment summary.
 *
 * @packageDocumentation
 */
import React from 'react';
import {Icon, Tooltip, Typography} from 'antd';
import {AssignmentPlan} from '../../../../../../common/flight-leg/assignment-plan';
import {RouteVisit} from '../../../../../../common/flight-leg/route-visits';
import {visitLabel} from '../../state/itinerary';

/** Inputs for the shared boarding/loading or arrival/unloading location. */
export interface JourneyEndpointProps {
    plan: AssignmentPlan;
    names: Map<string, string>;
    /** Specific occurrence, so repeated visits remain distinguishable. */
    visit: RouteVisit;
    /** Selects passenger or cargo wording. */
    kind: 'pax' | 'cgo';
    /** True uses red/up for getting on; false uses green/down for getting off. */
    boarding: boolean;
    /** Adds summary-only mobile action labels and repeated-visit labels. */
    withVisit?: boolean;
}

/**
 * Renders one endpoint with the dispatcher colors/icons and a one-second tooltip.
 *
 * @remarks
 * Visit choices use the compact location-only form. Final summary cells enable
 * action labels on mobile and occurrence labels when a location repeats.
 */
export default function JourneyEndpoint({
    plan,
    names,
    visit,
    kind,
    boarding,
    withVisit = false,
}: JourneyEndpointProps) {
    const location = names.get(visit.locationID) || visit.locationID;
    const action =
        kind === 'pax' ? (boarding ? 'Board at' : 'Leave at') : boarding ? 'Load at' : 'Unload at';
    const label = visitLabel(plan, visit.key);
    const help =
        kind === 'pax'
            ? `Passengers ${boarding ? 'board' : 'leave'} the aircraft at ${location}.`
            : `Cargo is ${boarding ? 'loaded onto' : 'unloaded from'} the aircraft at ${location}.`;

    return (
        <span className="summary-visit">
            {withVisit && <span className="summary-mobile-label">{action}</span>}
            <Tooltip mouseEnterDelay={1} title={help}>
                <Typography.Text strong className={boarding ? 'journey-board' : 'journey-leave'}>
                    <Icon type={boarding ? 'arrow-up' : 'arrow-down'} aria-label={action} />
                    {location}
                </Typography.Text>
            </Tooltip>
            {withVisit && label && (
                <Typography.Text type="secondary" className="summary-visit-label">
                    {label}
                </Typography.Text>
            )}
        </span>
    );
}
